/* Pine Park Space Run v2: audio (assets/game-audio.js, owner: audio).
   Every sound in docs/game-v2/design.md section 15, made by Tone.js at run time.
   PPG.audio.play(id, data) is the only SFX entry and PPG.audio.music(id) the only
   music entry; interface and budgets in docs/game-v2/contracts.md sections 7, 8, 11.
   - init() builds the whole graph once. Nothing here makes a Tone instrument, effect
     or bus after it (the one-shot oscillator Tone makes inside each note is Tone's own).
   - One voice pool per sound family. Every trigger sets what it needs on its own
     voice; nothing shared is mutated and then restored by a timer (v1 rule 38).
   - at() keeps each start on a voice strictly after its previous start, so Tone never
     throws "Start time must be strictly greater" when frames bunch up.
   - Music runs on its own clock, scheduled 0.12 s ahead from Tone's context tick, so a
     stalled frame never stutters the beat. SFX play at Tone.now() (lookAhead 0.02 s).
   - Sustained sounds have hard lengths and are cut on pause and on stopAll.
   - With no window.Tone every function is a safe no-op and the game runs silent. */
(function () {
  'use strict';
  var PPG = window.PPG = window.PPG || {};
  var has = Object.prototype.hasOwnProperty;

  var T = null, X = null;
  var ready = false, failed = false, halted = false;
  var muted = false, mutedRead = false, suspendId = -1;
  var trackId = 'off', lastId = '', want = '';
  /* The launch swell fires before the first PLAY's context is running (Tone.js
     loads in parallel and starts inside the gesture); it is held for 1.5 s and
     plays on the first running tick while the title is still up. */
  var introAt = -1;
  function nowMs() { return (window.performance && performance.now) ? performance.now() : 0; }
  var warned = {}, lastAt = {}, EMPTY = {};
  var HZ = [];
  var G = {}, P = {}, M = {}, L = {};
  var SUS = [], CONT = [], live = false;
  var AHEAD = 0.12;
  var seq = { on: false, paused: false, track: 'off', pending: '', next: 0, step: 0, bar: 0, bpm: 120 };

  /* ---------- small helpers (no allocation) ---------- */

  function noop() {}
  function warn(key, msg) {
    if (warned[key]) { return; }
    warned[key] = true;
    if (window.console && console.warn) { console.warn('\uD83D\uDD79\uFE0F PPG audio: ' + msg); }
  }
  function clamp(v, a, b) { return v < a ? a : (v > b ? b : v); }
  function hz(m) { return HZ[m] || 440 * Math.pow(2, (m - 69) / 12); }
  function rnd(a, b) { return a + Math.random() * (b - a); }
  function sgn(v) { return v < 0 ? -1 : (v > 0 ? 1 : 0); }

  /* A voice: { n: node, t: last start, h: sustained until }. at() is the start guard. */
  function at(v, t) {
    if (t <= v.t) { t = v.t + 0.003; }
    v.t = t;
    return t;
  }
  function pick(pool) {
    var b = pool[0], i;
    for (i = 1; i < pool.length; i++) { if (pool[i].t < b.t) { b = pool[i]; } }
    return b;
  }
  function adsr(e, a, d, s, r) { e.attack = a; e.decay = d; e.sustain = s; e.release = r; }
  function setAt(p, v, t) { p.cancelScheduledValues(t); p.setValueAtTime(v, t); }
  function sweep(p, a, b, t, d) {
    p.cancelScheduledValues(t);
    p.setValueAtTime(a, t);
    if (b !== a) { p.exponentialRampToValueAtTime(b, t + d); }
  }
  function lvl(g, v, t, r) { g.gain.linearRampTo(v, r || (v > 0 ? 0.05 : 0.25), t); }
  function tone(v, f, dur, t, vel) {
    t = at(v, t);
    v.n.triggerAttackRelease(f, dur, t, vel);
    return t;
  }
  function slide(v, f0, f1, g, dur, t, vel) {
    var fr = v.n.frequency;
    t = at(v, t);
    fr.cancelScheduledValues(t);
    v.n.triggerAttackRelease(f0, dur, t, vel);
    if (f1 !== f0) { fr.exponentialRampToValueAtTime(f1, t + g); }
    return t;
  }
  /* Noise voice: filter sweep f0 to f1 over g, Q q, envelope a d s r, held dur. */
  function nz(v, f0, f1, g, q, a, d, s, r, dur, t, vel, crv) {
    if (!v.cont) { t = run(v, t, Math.max(dur, a + d) + r + 0.05); } else if (t > v.t) { v.t = t; }
    if (v.c !== crv) { v.e.attackCurve = crv; v.c = crv; }
    adsr(v.e, a, d, s, r);
    sweep(v.f.frequency, f0, f1, t, g);
    setAt(v.f.Q, q, t);
    v.e.triggerAttackRelease(dur, t, vel);
    return v;
  }
  function cut(v, t) {
    v.h = 0;
    if (v.e) {
      v.e.cancel(t);
      v.e.release = 0.04;
      v.e.triggerRelease(t);
      if (v.s && !v.cont) { v.s.stop(t + 0.06); }
      if (v.k) { v.k.stop(t + 0.06); }
      return;
    }
    v.n.envelope.release = 0.04;
    v.n.triggerRelease(t);
  }
  function duck(depth, hold, rel, t) {
    var g = G.duck.gain;
    g.cancelAndHoldAtTime(t);
    g.linearRampToValueAtTime(depth, t + 0.02);
    g.linearRampToValueAtTime(depth, t + 0.02 + hold);
    g.linearRampToValueAtTime(1, t + 0.02 + hold + rel);
  }
  function screenAwake() {
    var s = PPG.state && PPG.state.screen;
    return s !== 'idle' && s !== 'exiting';
  }

  /* ---------- building blocks used once, by init ---------- */

  function osc(type, a, b) {
    if (type.indexOf('fat') === 0) { return { type: type, count: a, spread: b }; }
    if (type.indexOf('fm') === 0) { return { type: type, harmonicity: a, modulationIndex: b, modulationType: 'sine' }; }
    return { type: type };
  }
  function gain(g, out) { var n = new T.Gain(g); n.connect(out); return n; }
  function filt(type, f, q, out) {
    var n = new T.Filter({ type: type, frequency: f, Q: q, rolloff: -12 });
    n.connect(out);
    return n;
  }
  function syn(o, vol, out) {
    var s = new T.Synth({ oscillator: o, envelope: { attack: 0.002, decay: 0.1, sustain: 0, release: 0.05 }, volume: vol });
    s.connect(out);
    return { n: s, t: -1, h: 0 };
  }
  function mono(type, vol, out, q, base, oct) {
    var s = new T.MonoSynth({
      oscillator: osc(type, 2, 16),
      envelope: { attack: 0.005, decay: 0.2, sustain: 0.4, release: 0.15 },
      filter: { type: 'lowpass', Q: q, rolloff: -12 },
      filterEnvelope: { attack: 0.005, decay: 0.2, sustain: 0.3, release: 0.2, baseFrequency: base, octaves: oct },
      volume: vol
    });
    s.connect(out);
    return { n: s, t: -1, h: 0 };
  }
  function drum(vol, out) {
    var s = new T.MembraneSynth({ pitchDecay: 0.05, octaves: 5, oscillator: { type: 'sine' }, envelope: { attack: 0.001, decay: 0.4, sustain: 0, release: 0.1 }, volume: vol });
    s.connect(out);
    return { n: s, t: -1, h: 0 };
  }
  /* A noise (or tick) voice with its own looping source. Frequent SFX voices (cont) keep
     theirs running while audio is live, so a trigger allocates nothing; rare voices run
     theirs only while they sound. After stopAll every source is stopped. */
  function nzv(buf, type, f, out, pan, dist) {
    var e = new T.AmplitudeEnvelope({ attack: 0.002, decay: 0.1, sustain: 0, release: 0.05 });
    var fl = new T.Filter({ type: type, frequency: f, Q: 1, rolloff: -12 });
    var rec = { e: e, f: fl, p: null, d: null, o: fl, s: null, t: -1, h: 0, c: 'linear' };
    if (buf) { rec.s = new T.Player({ url: buf, loop: true }); rec.s.connect(e); }
    e.connect(fl);
    if (dist) { rec.d = new T.Distortion({ distortion: dist, oversample: 'none', wet: 0 }); rec.o.connect(rec.d); rec.o = rec.d; }
    if (pan) { rec.p = new T.Panner(0); rec.o.connect(rec.p); rec.o = rec.p; }
    rec.o.connect(out);
    return rec;
  }
  function sawv(vol, pan, dist) {
    var fl = new T.Filter({ type: 'lowpass', frequency: 1200, Q: 1, rolloff: -24 });
    var rec = syn(osc('fatsawtooth', 3, 30), vol, fl), last = fl;
    rec.f = fl;
    if (dist) { rec.d = new T.Distortion({ distortion: 0.6, oversample: 'none', wet: 0 }); last.connect(rec.d); last = rec.d; }
    if (pan) { rec.p = new T.Panner(0); last.connect(rec.p); last = rec.p; }
    last.connect(G.sfx);
    return rec;
  }
  function noiseBuf(c, pink) {
    var sr = c.sampleRate, len = Math.floor(sr * 1.3), buf = c.createBuffer(2, len, sr);
    var ch, d, i, w, b0, b1, b2;
    for (ch = 0; ch < 2; ch++) {
      d = buf.getChannelData(ch);
      b0 = 0; b1 = 0; b2 = 0;
      for (i = 0; i < len; i++) {
        w = Math.random() * 2 - 1;
        if (pink) {
          b0 = 0.99765 * b0 + w * 0.099046;
          b1 = 0.963 * b1 + w * 0.2965164;
          b2 = 0.57 * b2 + w * 1.0526913;
          d[i] = (b0 + b1 + b2 + w * 0.1848) * 0.22;
        } else {
          d[i] = w * 0.6;
        }
      }
    }
    return buf;
  }
  /* A small dark plate: decaying noise that loses its top end as it fades. */
  function irBuf(c, sec) {
    var sr = c.sampleRate, len = Math.floor(sr * sec), buf = c.createBuffer(2, len, sr);
    var ch, d, i, k, y, env, fall = Math.exp(Math.log(0.001) / (len * 0.8)), fade = sr * 0.008;
    for (ch = 0; ch < 2; ch++) {
      d = buf.getChannelData(ch);
      y = 0;
      env = 1;
      for (i = 0; i < len; i++) {
        k = i / len;
        y += (0.9 - 0.78 * k) * ((Math.random() * 2 - 1) - y);
        env *= fall;
        d[i] = y * env * (i < fade ? i / fade : 1);
      }
    }
    return buf;
  }

  function build() {
    var c = X, i;
    for (i = 0; i < 128; i++) { HZ[i] = 440 * Math.pow(2, (i - 69) / 12); }
    /* buses -> glue -> limiter -> bg -> mute -> master -> destination (bg: hidden tab) */
    G.master = new T.Volume(-2);
    G.master.toDestination();
    G.mute = gain(muted ? 0 : 1, G.master);
    G.bg = gain(1, G.mute);
    G.limit = new T.Limiter(-1);
    G.limit.connect(G.bg);
    G.glue = new T.Compressor({ threshold: -16, ratio: 2.5, attack: 0.005, release: 0.16, knee: 8 });
    G.glue.connect(G.limit);
    G.sfxGate = gain(1, G.glue);
    G.sfx = new T.Volume(0);
    G.sfx.connect(G.sfxGate);
    G.musGate = gain(1, G.glue);
    G.duck = gain(1, G.musGate);
    G.pause = gain(1, G.duck);
    G.musLP = filt('lowpass', 18000, 0.5, G.pause);
    G.mus = new T.Volume(-7);
    G.mus.connect(G.musLP);
    G.verbRet = gain(1, G.glue);
    G.verb = new T.Convolver(irBuf(c, 1.9));
    G.verb.connect(G.verbRet);
    G.verbIn = gain(0.8, G.verb);
    G.musSend = gain(1, G.verbIn);
    G.dly = new T.PingPongDelay({ delayTime: 0.19, feedback: 0.3, wet: 1, maxDelay: 0.5 });
    G.dly.connect(G.sfx);
    G.dlyIn = gain(0.5, G.dly);
    G.white = noiseBuf(c, false);
    G.pink = noiseBuf(c, true);
    buildSfx();
    buildMusic();
  }

  function buildSfx() {
    var sfx = G.sfx, i, v, send;
    G.blipLP = filt('lowpass', 4200, 0.7, sfx);
    G.blipPan = new T.Panner(0);
    G.blipPan.connect(G.blipLP);
    P.blip = [syn(osc('square'), -7, G.blipPan), syn(osc('square'), -7, G.blipPan)];
    P.tone = [syn(osc('sine'), -9, sfx), syn(osc('sine'), -9, sfx)];
    G.bellVerb = gain(0.35, G.verbIn);
    G.bellDly = gain(0.6, G.dlyIn);
    P.bell = [];
    for (i = 0; i < 3; i++) {
      v = syn(osc('fmsine', 3.5, 6), -13, sfx);
      v.n.connect(G.bellVerb);
      v.n.connect(G.bellDly);
      P.bell.push(v);
    }
    G.coinLP = filt('lowpass', 6500, 0.6, sfx);
    G.coinLP.connect(gain(0.35, G.dlyIn));
    P.coin = [syn(osc('square'), -12, G.coinLP), syn(osc('square'), -12, G.coinLP)];
    send = gain(0.3, G.verbIn);
    P.brass = [mono('sawtooth', -13, sfx, 1.4, 400, 3.2), mono('sawtooth', -13, sfx, 1.4, 400, 3.2)];
    P.brass[0].n.connect(send);
    P.brass[1].n.connect(send);
    G.chordLP = filt('lowpass', 3000, 0.8, sfx);
    G.chordLP.connect(gain(0.4, G.verbIn));
    P.chord = [];
    for (i = 0; i < 4; i++) { P.chord.push(syn(osc('fatsawtooth', 2, 18), -19, G.chordLP)); }
    P.sawA = sawv(-14, false, false);
    P.sawB = sawv(-12, true, true);
    P.sub = [syn(osc('sine'), -3, sfx)];
    P.kick = [drum(-4, sfx), drum(-4, sfx)];
    send = gain(0.4, G.verbIn);
    P.metal = [syn(osc('fmsquare', 1.41, 12), -20, sfx), syn(osc('fmsquare', 1.41, 12), -20, sfx)];
    P.metal[0].n.connect(send);
    P.metal[1].n.connect(send);
    P.hi = [nzv(G.white, 'bandpass', 3000, sfx, true, 0), nzv(G.white, 'bandpass', 3000, sfx, true, 0)];
    CONT = [P.hi[0], P.hi[1]];
    send = gain(0.3, G.verbIn);
    P.wh = [nzv(G.pink, 'bandpass', 1000, sfx, false, 0), nzv(G.pink, 'bandpass', 1000, sfx, false, 0)];
    P.wh[0].o.connect(send);
    P.wh[1].o.connect(send);
    CONT.push(P.wh[0], P.wh[1]);
    send = gain(0.25, G.verbIn);
    P.lo = [nzv(G.white, 'lowpass', 2000, sfx, false, 0.75), nzv(G.white, 'lowpass', 2000, sfx, false, 0.75)];
    P.lo[0].o.connect(send);
    P.lo[1].o.connect(send);
    CONT.push(P.lo[0], P.lo[1]);
    /* tick trains: a source run per train and gated by envelope events (cancellable) */
    P.tickA = nzv(null, 'bandpass', 2400, sfx, false, 0);
    P.tickA.k = new T.Oscillator({ type: 'square', frequency: 1200, volume: -11 });
    P.tickA.k.connect(P.tickA.e);
    P.tickB = nzv(G.white, 'bandpass', 2000, sfx, true, 0);
    P.tickB.k = new T.Oscillator({ type: 'sawtooth', frequency: 150, volume: -12 });
    G.tickBo = gain(0.5, P.tickB.e);
    G.tickBn = gain(1, P.tickB.e);
    P.tickB.s.disconnect();
    P.tickB.s.connect(G.tickBn);
    P.tickB.k.connect(G.tickBo);
    P.tickB.o.connect(gain(0.3, G.verbIn));
    SUS = [P.sawA, P.sawB, P.wh[0], P.wh[1], P.lo[0], P.lo[1], P.tickA, P.tickB];
  }

  function buildMusic() {
    var mus = G.mus, i;
    L.pad = gain(0, mus); L.sub = gain(0, mus); L.bass = gain(0, mus); L.arp = gain(0, mus);
    L.lead = gain(0, mus); L.drum = gain(0, mus); L.fx = gain(0, mus);
    L.pad.connect(gain(0.32, G.musSend));
    L.arp.connect(gain(0.18, G.musSend));
    L.lead.connect(gain(0.3, G.musSend));
    L.fx.connect(gain(0.3, G.musSend));
    G.mdly = new T.FeedbackDelay({ delayTime: 0.375, feedback: 0.38, wet: 1, maxDelay: 1 });
    G.mdly.connect(mus);
    L.arp.connect(gain(0.32, G.mdly));
    L.lead.connect(gain(0.22, G.mdly));
    /* pad: dry, or through an "ah" formant bank for the boss choir */
    G.pump = gain(1, L.pad);
    G.padDry = gain(1, G.pump);
    G.padForm = gain(0, G.pump);
    G.padLP = filt('lowpass', 900, 0.9, G.padDry);
    G.padLP.connect(filt('bandpass', 800, 5, G.padForm));
    G.padLP.connect(filt('bandpass', 1150, 7, G.padForm));
    G.padLP.connect(filt('bandpass', 2900, 9, G.padForm));
    M.pad = [];
    for (i = 0; i < 4; i++) { M.pad.push(syn(osc('fatsawtooth', 3, 22), -21, G.padLP)); }
    M.sub = syn(osc('sine'), -8, L.sub);
    G.bassDist = new T.Distortion({ distortion: 0.55, oversample: 'none', wet: 0 });
    G.bassDist.connect(L.bass);
    M.bass = mono('square', -10, G.bassDist, 2.5, 160, 3.4);
    M.arp = syn(osc('triangle'), -13, L.arp);
    adsr(M.arp.n.envelope, 0.002, 0.14, 0, 0.05);
    M.bell = syn(osc('fmsine', 3.01, 5), -12, L.arp);
    adsr(M.bell.n.envelope, 0.001, 0.9, 0, 0.3);
    M.lead = mono('fatsawtooth', -15, L.lead, 1.6, 700, 2.6);
    M.lead.n.portamento = 0.045;
    adsr(M.lead.n.envelope, 0.012, 0.3, 0.7, 0.25);
    adsr(M.lead.n.filterEnvelope, 0.02, 0.4, 0.6, 0.3);
    G.drumVerb = gain(0.22, G.musSend);
    M.kick = drum(-3, L.drum);
    M.kick.n.pitchDecay = 0.045;
    M.kick.n.octaves = 6;
    adsr(M.kick.n.envelope, 0.001, 0.42, 0, 0.1);
    M.tom = drum(-8, L.drum);
    M.tom.n.pitchDecay = 0.07;
    M.tom.n.octaves = 2.2;
    adsr(M.tom.n.envelope, 0.001, 0.32, 0, 0.1);
    M.tom.n.connect(G.drumVerb);
    /* one noise loop feeds clap, hat and crash; it runs only while a drum track plays */
    M.drums = { s: new T.Player({ url: G.white, loop: true }), t: -1, on: false };
    M.clap = nzv(null, 'bandpass', 1300, L.drum, false, 0);
    M.clap.f.Q.value = 1.1;
    M.clap.f.connect(G.drumVerb);
    M.hat = nzv(null, 'highpass', 7500, L.drum, false, 0);
    M.crash = nzv(null, 'highpass', 4500, L.drum, false, 0);
    M.crash.f.connect(G.drumVerb);
    M.drums.s.connect(M.clap.e);
    M.drums.s.connect(M.hat.e);
    M.drums.s.connect(M.crash.e);
    G.sirenLP = filt('lowpass', 1300, 3, L.fx);
    M.siren = syn(osc('square'), -21, G.sirenLP);
    adsr(M.siren.n.envelope, 0.3, 0.2, 0.85, 0.4);
    M.bed = nzv(G.pink, 'bandpass', 400, L.fx, false, 0);
  }

  function goLive(t) {
    var i;
    live = true;
    for (i = 0; i < CONT.length; i++) { CONT[i].cont = true; CONT[i].s.start(at(CONT[i], t), Math.random()); }
  }
  function goQuiet(t) {
    var i;
    live = false;
    for (i = 0; i < CONT.length; i++) { CONT[i].s.stop(t); }
  }
  /* Run a voice's own sources from t for len s (t guarded); returns the start time. */
  function run(v, t, len) {
    t = at(v, t);
    if (v.s) { v.s.start(t, Math.random()); v.s.stop(t + len); }
    if (v.k) { v.k.start(t); v.k.stop(t + len); }
    return t;
  }

  /* ---------- music: tracks, patterns, the look-ahead clock ---------- */

  /* Chords: Am F C G (two bars each), A Phrygian Am Bb Gm Am, and A major A D F#m E (one bar each). */
  var PROG = [
    { bars: 2, pad: [[57, 60, 64, 67], [53, 57, 60, 64], [55, 60, 64, 67], [55, 59, 62, 67]], root: [33, 29, 36, 31] },
    { bars: 2, pad: [[57, 60, 64, 69], [58, 62, 65, 70], [55, 58, 62, 67], [57, 60, 64, 69]], root: [33, 34, 31, 33] },
    { bars: 1, pad: [[57, 61, 64, 69], [57, 62, 66, 69], [57, 61, 66, 69], [56, 59, 64, 68]], root: [33, 38, 42, 40] }
  ];
  var ARP = [0, 1, 2, 3, 1, 2, 3, 4, 2, 3, 4, 3, 2, 1, 0, 1];
  var BELLS = [0, 2, 1, 3, 2, 4, 3, 1];
  var BOSSBASS = [0, 0, 12, 0, 0, 12, 0, 0, 0, 0, 12, 0, 0, 12, 0, 7];
  var TOMS = [50, 47, 43, 40];
  /* The phase 3 hook: [step, midi, length in steps] per bar, a 4-bar phrase answered over C and G. */
  var LEAD = [
    [[0, 69, 4], [4, 72, 2], [6, 76, 6], [12, 74, 2], [14, 72, 2]],
    [[0, 71, 4], [4, 72, 2], [6, 69, 10]],
    [[0, 69, 2], [2, 72, 2], [4, 77, 4], [8, 76, 4], [12, 72, 4]],
    [[0, 74, 6], [6, 72, 2], [8, 69, 8]],
    [[0, 67, 4], [4, 72, 2], [6, 76, 6], [12, 74, 2], [14, 72, 2]],
    [[0, 74, 4], [4, 76, 2], [6, 79, 10]],
    [[0, 71, 2], [2, 74, 2], [4, 79, 4], [8, 77, 4], [12, 74, 4]],
    [[0, 76, 6], [6, 74, 2], [8, 71, 4], [12, 74, 2], [14, 76, 2]]
  ];
  var DRONE = [45, 52, 57, 60];
  /* groove: changes from it wait for the next bar. lp: pad low-pass. bass: 1 eighths,
     2 eighths with octave jumps, 3 quarters, 4 distorted sixteenths. */
  var TR = {
    off: { bpm: 120 },
    lobby: { bpm: 120, on: 1, prog: 0, pad: 1, lp: 900, padA: 0.9, sub: 1 },
    phase_1: { bpm: 120, on: 1, groove: 1, prog: 0, pad: 1, lp: 1600, padA: 0.25, bass: 1, kick: 1 },
    phase_2: { bpm: 120, on: 1, groove: 1, prog: 0, pad: 1, lp: 2100, padA: 0.25, bass: 1, kick: 2, clap: 1, arp: 1, enter: 1 },
    phase_3: { bpm: 120, on: 1, groove: 1, prog: 0, pad: 1, lp: 2700, padA: 0.25, bass: 2, kick: 2, clap: 1, arp: 1, lead: 1, hat: 1, crash: 1, enter: 1 },
    warning: { bpm: 120, on: 1, prog: 0, pad: 1, lp: 1100, padA: 0.6, sub: 1, siren: 1 },
    boss: { bpm: 128, on: 1, groove: 1, prog: 1, pad: 1, form: 1, lp: 3400, padA: 0.45, bass: 4, kick: 2, clap: 1, hat: 2, tom: 1, crash: 1, enter: 1 },
    kill: { bpm: 120 },
    victory: { bpm: 120, on: 1, groove: 1, prog: 2, pad: 1, lp: 2600, padA: 0.35, bass: 3, kick: 3, clap: 2, bell: 1, crash: 2, enter: 1 },
    gameover: { bpm: 120 }
  };
  var IMMEDIATE = { kill: 1, gameover: 1, lobby: 1 };

  function padChord(tones, t, dur, a) {
    var i, v;
    for (i = 0; i < 4; i++) {
      v = M.pad[i];
      adsr(v.n.envelope, a, 0.6, 0.72, a > 0.5 ? 1.6 : 1.0);
      tone(v, hz(tones[i]), dur, t, 0.62);
    }
  }
  function kickAt(t, vel) {
    tone(M.kick, 55, 0.2, t, vel);
    var g = G.pump.gain;
    g.setValueAtTime(1 - 0.55 * vel, t);
    g.linearRampToValueAtTime(1, t + 0.22);
  }
  function clapAt(t, vel) {
    var e = M.clap.e;
    adsr(e, 0.001, 0.008, 0, 0.008);
    e.triggerAttackRelease(0.004, t, vel * 0.55);
    e.triggerAttackRelease(0.004, t + 0.011, vel * 0.6);
    adsr(e, 0.001, 0.15, 0, 0.05);
    e.triggerAttackRelease(0.02, t + 0.022, vel);
  }
  function hatAt(t, vel, open) {
    adsr(M.hat.e, 0.001, open ? 0.24 : 0.045, 0, open ? 0.08 : 0.02);
    M.hat.e.triggerAttackRelease(open ? 0.05 : 0.01, t, vel);
  }
  function crashAt(t, vel) {
    adsr(M.crash.e, 0.002, 1.7, 0, 0.4);
    M.crash.e.triggerAttackRelease(0.05, t, vel);
  }
  function bassAt(m, dur, t, vel) { tone(M.bass, hz(m), dur, t, vel); }
  /* The drum noise loop: on for drum tracks, off (after the crash tail) otherwise. */
  function drumNoise(on, t, tail) {
    var d = M.drums;
    if (on && !d.on) { d.s.start(at(d, t), Math.random()); }
    if (!on && d.on) { d.s.stop(t + tail); }
    d.on = on;
  }

  function patterns(tr, s, bar, t) {
    var p = PROG[tr.prog], sd = 15 / seq.bpm, ci = Math.floor(bar / p.bars) % 4;
    var tones = p.pad[ci], root = p.root[ci], m, i, ev, k;
    if (tr.pad && s === 0 && bar % p.bars === 0) { padChord(tones, t, p.bars * 16 * sd - 0.04, tr.padA); }
    if (tr.sub && (s === 0 || s === 8)) { tone(M.sub, hz(root), 7 * sd, t, 0.8); }
    k = tr.bass;
    if ((k === 1 || k === 2) && s % 2 === 0) {
      m = root + 12 + (k === 2 && s % 4 === 2 ? 12 : 0);
      bassAt(m, sd * 1.6, t, s % 4 === 0 ? 0.85 : 0.62);
    } else if (k === 3 && s % 4 === 0) {
      bassAt(root + 12, sd * 3, t, 0.8);
    } else if (k === 4) {
      bassAt(root + 12 + BOSSBASS[s], sd * 0.8, t, s % 4 === 0 ? 0.95 : 0.6);
    }
    if (tr.arp) {
      i = ARP[s];
      m = (i > 3 ? tones[0] + 12 : tones[i]) + 12;
      tone(M.arp, hz(m), sd * 0.9, t, s % 4 === 0 ? 0.72 : 0.5);
    }
    if (tr.bell && s % 2 === 0) {
      i = BELLS[s >> 1];
      m = (i > 3 ? tones[0] + 12 : tones[i]) + 12;
      tone(M.bell, hz(m), sd * 3, t, s % 4 === 0 ? 0.55 : 0.4);
    }
    if (tr.lead) {
      ev = LEAD[bar % 8];
      for (i = 0; i < ev.length; i++) {
        if (ev[i][0] === s) { tone(M.lead, hz(ev[i][1]), ev[i][2] * sd * 0.92, t, 0.8); }
      }
    }
    k = tr.kick;
    if ((k === 1 && (s === 0 || s === 8)) || (k === 2 && s % 4 === 0) || (k === 3 && s === 0)) {
      kickAt(t, k === 1 ? 0.62 : 0.95);
    }
    if ((tr.clap === 1 && (s === 4 || s === 12)) || (tr.clap === 2 && s === 8)) { clapAt(t, 0.7); }
    if (tr.hat === 1 && s % 2 === 0) { hatAt(t, s % 4 === 2 ? 0.42 : 0.2, s % 4 === 2); }
    if (tr.hat === 2) { hatAt(t, s % 4 === 2 ? 0.3 : 0.13, s === 14); }
    if (s === 0 && ((tr.crash === 1 && bar % 8 === 0) || (tr.crash === 2 && bar % 4 === 0))) { crashAt(t, tr.crash === 1 ? 0.5 : 0.3); }
    if (tr.tom && bar % 4 === 3 && s >= 8 && s % 2 === 0) { tone(M.tom, hz(TOMS[(s - 8) >> 1]), 0.2, t, 0.85); }
    if (tr.siren && s === 0) {
      var f = M.siren.n.frequency, len = 16 * sd;
      f.cancelScheduledValues(t);
      f.setValueAtTime(440, t);
      f.exponentialRampToValueAtTime(622, t + len * 0.5);
      f.exponentialRampToValueAtTime(440, t + len);
    }
  }

  function bassMode(k, t) {
    var b = M.bass.n, fe = b.filterEnvelope;
    if (k === 4) {
      b.oscillator.type = 'sawtooth';
      setAt(G.bassDist.wet, 0.85, t);
      fe.baseFrequency = 220; fe.octaves = 3;
      adsr(b.envelope, 0.002, 0.12, 0.3, 0.05);
    } else if (k === 3) {
      b.oscillator.type = 'triangle';
      setAt(G.bassDist.wet, 0, t);
      fe.baseFrequency = 300; fe.octaves = 2;
      adsr(b.envelope, 0.004, 0.3, 0.4, 0.2);
    } else {
      b.oscillator.type = 'square';
      setAt(G.bassDist.wet, 0, t);
      fe.baseFrequency = 160; fe.octaves = 3.4;
      adsr(b.envelope, 0.002, 0.2, 0.25, 0.08);
    }
    adsr(fe, 0.002, 0.16, 0.15, 0.1);
  }
  function allDetune(t) {
    var i;
    for (i = 0; i < 4; i++) { setAt(M.pad[i].n.detune, 0, t); }
    setAt(M.bass.n.detune, 0, t);
    setAt(M.lead.n.detune, 0, t);
    setAt(M.arp.n.detune, 0, t);
    setAt(M.sub.n.detune, 0, t);
  }

  function applyTrack(id, t) {
    var tr = TR[id], i;
    seq.bpm = tr.bpm;
    lvl(G.musGate, 1, t, 0.04);
    lvl(G.musSend, seq.paused ? 0.3 : 1, t, 0.04);
    lvl(L.pad, tr.pad ? 1 : 0, t);
    lvl(L.sub, tr.sub ? 1 : 0, t);
    lvl(L.bass, tr.bass ? 1 : 0, t);
    lvl(L.arp, (tr.arp || tr.bell) ? 1 : 0, t);
    lvl(L.lead, tr.lead ? 1 : 0, t);
    lvl(L.drum, tr.kick ? 1 : 0, t);
    drumNoise(!!tr.kick, t, 2.3);
    lvl(L.fx, tr.siren ? 1 : 0, t);
    setAt(G.padDry.gain, tr.form ? 0 : 1, t);
    setAt(G.padForm.gain, tr.form ? 2.6 : 0, t);
    if (tr.lp) { G.padLP.frequency.exponentialRampTo(tr.lp, 1.2, t); }
    allDetune(t);
    if (tr.bass) { bassMode(tr.bass, t); }
    adsr(M.sub.n.envelope, 0.06, 0.5, 0.3, 0.4);
    if (tr.siren) {
      for (i = 0; i < 4; i++) { M.pad[i].n.detune.linearRampToValueAtTime(-70, t + 4); }
      tone(M.siren, 440, 9, t, 0.7);
    } else {
      M.siren.n.triggerRelease(t);
    }
    if (!tr.pad) { for (i = 0; i < 4; i++) { M.pad[i].n.triggerRelease(t); } }
    cut(M.bed, t);
    if (id === 'kill') { killTrack(t); }
    if (id === 'gameover') { overTrack(t); }
  }
  /* kill: the groove drops out, a held low A under a rising noise bed, silent at +2.2 s */
  function killTrack(t) {
    lvl(L.pad, 0, t, 0.06); lvl(L.arp, 0, t, 0.06); lvl(L.lead, 0, t, 0.06); lvl(L.drum, 0, t, 0.06);
    lvl(L.bass, 1, t, 0.02); lvl(L.sub, 1, t, 0.02); lvl(L.fx, 1, t, 0.02);
    bassMode(4, t);
    setAt(G.bassDist.wet, 0.3, t);
    M.bass.n.filterEnvelope.baseFrequency = 140;
    M.bass.n.filterEnvelope.octaves = 1.5;
    adsr(M.bass.n.envelope, 0.02, 0.3, 0.9, 0.05);
    adsr(M.bass.n.filterEnvelope, 0.02, 1.8, 0.5, 0.05);
    tone(M.bass, hz(33), 2.13, t, 0.85);
    adsr(M.sub.n.envelope, 0.02, 0.3, 0.9, 0.05);
    tone(M.sub, hz(33), 2.13, t, 0.9);
    nz(M.bed, 300, 5000, 2.15, 1.2, 2.0, 0.1, 0.8, 0.05, 2.12, t, 0.75, 'exponential');
    hush(L.bass, t + 2.12); hush(L.sub, t + 2.12); hush(L.fx, t + 2.12);
  }
  function hush(g, t) { g.gain.setValueAtTime(1, t); g.gain.linearRampToValueAtTime(0, t + 0.08); }
  /* gameover: tape-stop the groove, then the pad falls a minor third over 2 s into a drone */
  function overTrack(t) {
    var i, v, ts = 0.7, td = t + ts + 0.05;
    for (i = 0; i < 4; i++) { M.pad[i].n.detune.linearRampTo(-1200, ts, t); }
    M.bass.n.detune.linearRampTo(-1200, ts, t);
    M.lead.n.detune.linearRampTo(-1200, ts, t);
    M.arp.n.detune.linearRampTo(-1200, ts, t);
    lvl(L.bass, 0, t, ts); lvl(L.lead, 0, t, ts); lvl(L.arp, 0, t, ts); lvl(L.drum, 0, t, ts * 0.6); lvl(L.sub, 0, t, ts);
    lvl(L.pad, 0, t, ts);
    L.pad.gain.linearRampToValueAtTime(1, td + 0.8);
    G.padLP.frequency.exponentialRampTo(900, 2.5, t + ts);
    for (i = 0; i < 4; i++) {
      v = M.pad[i];
      v.n.detune.setValueAtTime(0, td - 0.01);
      v.n.detune.linearRampToValueAtTime(-300, td + 2.0);
      adsr(v.n.envelope, 0.4, 1.0, 0.7, 2.5);
      tone(v, hz(DRONE[i]), 30, td, 0.7);
    }
  }

  function stepAt(t) {
    var s = seq.step, tr;
    if (s === 0 && seq.pending) {
      seq.track = seq.pending;
      seq.pending = '';
      applyTrack(seq.track, t);
      if (TR[seq.track].enter) { crashAt(t, 0.45); }
    }
    tr = TR[seq.track];
    if (tr.on) { patterns(tr, s, seq.bar, t); }
    seq.step = s === 15 ? 0 : s + 1;
    if (seq.step === 0) { seq.bar++; }
  }
  function switchNow(id) {
    var t = X.currentTime + 0.02;
    seq.pending = '';
    seq.track = id;
    seq.step = 0;
    seq.bar = 0;
    applyTrack(id, t);
    seq.on = !!TR[id].on;
    seq.next = t;
  }
  function route(id) {
    if (id === seq.track) { seq.pending = ''; return; }
    if (IMMEDIATE[id] || !seq.on || !TR[seq.track].groove) { switchNow(id); } else { seq.pending = id; }
  }
  function releaseMusic(t) {
    var i;
    for (i = 0; i < 4; i++) { M.pad[i].n.triggerRelease(t); }
    M.sub.n.triggerRelease(t);
    M.bass.n.triggerRelease(t);
    M.lead.n.triggerRelease(t);
    M.siren.n.triggerRelease(t);
    cut(M.bed, t);
    drumNoise(false, t, 0.15);
  }
  function musicOff() {
    var now = X.currentTime;
    seq.on = false;
    seq.pending = '';
    seq.track = 'off';
    want = '';
    lvl(G.musGate, 0, now, 0.1);
    lvl(G.musSend, 0, now, 0.1);
    releaseMusic(now);
  }
  function onTick() {
    if (!ready) { return; }
    var c = X, now, n = 0, w;
    if (c.state !== 'running') { return; }
    if (!live && !halted) { goLive(c.currentTime + 0.01); }
    if (want && !halted) {
      w = want;
      want = '';
      try { route(w); } catch (e) { warn('music', 'music failed'); }
    }
    if (introAt >= 0) {
      w = PPG.state ? PPG.state.screen : '';
      if (!halted && !muted && nowMs() - introAt < 1500 && (w === 'intro' || w === 'callsign' || w === 'countdown')) {
        introAt = -1;
        /* through the public entry, so a wrapper (the harness sound log) sees it */
        if (PPG.audio && typeof PPG.audio.play === 'function') { PPG.audio.play('intro'); } else { play('intro'); }
      }
      introAt = -1;
    }
    if (!seq.on || seq.paused) { return; }
    now = c.currentTime;
    if (seq.next < now) { seq.next = now + 0.005; }
    while (seq.next < now + AHEAD && n < 12) {
      try { stepAt(seq.next); } catch (e) { warn('seq', 'a music step failed'); }
      seq.next += 15 / seq.bpm;
      n++;
    }
  }

  /* ---------- SFX: one function per id in design.md section 15 ---------- */

  var GRAZE = [81, 84, 86, 88, 91, 93, 96, 98, 100, 103];
  var CRATE = [[76, 81], [79, 84], [81, 86]];
  var SKY = [[81, 88], [84, 91], [86, 93]];
  var INTRO = [57, 64, 71, 76], GO = [69, 76, 81, 85], CPCH = [69, 76, 79, 84], UPCH = [69, 73, 76, 81];
  var OVER = [76, 72, 69], OVERT = [0, 0.24, 0.48], OVERD = [0.2, 0.2, 0.75];

  function blip(f0, f1, dur, t, vel, pan) {
    var v = pick(P.blip);
    adsr(v.n.envelope, 0.002, dur, 0, 0.03);
    t = slide(v, f0, f1, dur, dur, t, vel);
    setAt(G.blipPan.pan, pan || 0, t);
    return t;
  }
  function sine(f0, f1, dur, t, vel) {
    var v = pick(P.tone);
    adsr(v.n.envelope, 0.002, dur, 0, 0.02);
    v.h = 0;
    return slide(v, f0, f1, dur, dur, t, vel);
  }
  function bell(m, dur, t, vel) {
    var v = pick(P.bell);
    adsr(v.n.envelope, 0.001, dur, 0, 0.15);
    return tone(v, hz(m), dur, t, vel);
  }
  function kick(f, oct, pd, dec, t, vel) {
    var v = pick(P.kick);
    v.n.octaves = oct;
    v.n.pitchDecay = pd;
    adsr(v.n.envelope, 0.001, dec, 0, 0.05);
    return tone(v, f, dec, t, vel);
  }
  function metal(f0, f1, dec, t, vel) {
    var v = pick(P.metal);
    adsr(v.n.envelope, 0.001, dec, 0, 0.1);
    return slide(v, f0, f1, dec, dec, t, vel);
  }
  function sub(f0, f1, dec, t, vel) {
    var v = P.sub[0];
    adsr(v.n.envelope, 0.002, dec, 0, 0.1);
    return slide(v, f0, f1, dec, dec, t, vel);
  }
  function hi(f0, f1, g, q, d, t, vel, pan) {
    var v = pick(P.hi);
    setAt(v.p.pan, pan, t);
    return nz(v, f0, f1, g, q, 0.001, d, 0, 0.02, d * 0.6, t, vel, 'linear');
  }
  function wh(f0, f1, g, q, a, d, s, dur, t, vel) {
    var v = pick(P.wh);
    v.h = 0;
    return nz(v, f0, f1, g, q, a, d, s, 0.12, dur, t, vel, 'linear');
  }
  function lo(f0, f1, g, dist, d, s, dur, t, vel) {
    var v = pick(P.lo);
    v.h = 0;
    setAt(v.d.wet, dist, t);
    return nz(v, f0, f1, g, 0.7, 0.001, d, s, 0.08, dur, t, vel, 'linear');
  }
  /* sawA / sawB: fat saw through its own resonant low-pass (B also has drive and pan). */
  function saw(v, f0, f1, g, lp0, lp1, q, a, d, s, r, dur, t, vel) {
    adsr(v.n.envelope, a, d, s, r);
    t = slide(v, f0, f1, g, dur, t, vel);
    sweep(v.f.frequency, lp0, lp1, t, g);
    setAt(v.f.Q, q, t);
    return t;
  }
  function bDrive(w, pan, t) { setAt(P.sawB.d.wet, w, t); setAt(P.sawB.p.pan, pan, t); }
  /* Tick train on the voice's own source: envelope ticks from t over len s,
     rate r0 to r1 per second, pitch f0 to f1 (0 keeps the source pitch), tick decay tl. */
  function ticks(v, src, t, len, r0, r1, f0, f1, vel, jitter, tl) {
    var tt = 0, k = 0, rate, f;
    t = run(v, t, len + 0.08);
    v.e.cancel(t);
    adsr(v.e, 0.001, tl, 0, 0.008);
    if (src && f0) { src.frequency.cancelScheduledValues(t); }
    while (tt < len && k < 64) {
      rate = r0 + (r1 - r0) * (tt / len);
      if (src && f0) {
        f = jitter ? f0 + Math.floor(Math.random() * 6) * (f1 - f0) / 5 : f0 + (f1 - f0) * (tt / len);
        src.frequency.setValueAtTime(f, t + tt);
      }
      v.e.triggerAttackRelease(tl * 0.6, t + tt, vel * (jitter ? rnd(0.6, 1) : 1));
      tt += 1 / rate;
      k++;
    }
    v.h = t + len;
  }

  var SFX = {
    intro: function (t) {
      var i, v, t2;
      sweep(G.chordLP.frequency, 300, 3200, t, 1.1);
      setAt(G.chordLP.Q, 1.2, t);
      for (i = 0; i < 4; i++) {
        v = P.chord[i];
        adsr(v.n.envelope, 0.55, 0.5, 0.5, 1.1);
        t2 = tone(v, hz(INTRO[i]), 0.95, t, 0.5);
        v.n.detune.cancelScheduledValues(t2);
        v.n.detune.setValueAtTime(-120, t2);
        v.n.detune.linearRampToValueAtTime(0, t2 + 0.85);
      }
      wh(400, 3200, 1.0, 0.8, 0.7, 0.4, 0, 0.8, t, 0.35);
      bell(88, 0.8, t + 0.8, 0.25);
      bell(95, 0.8, t + 0.92, 0.16);
    },
    callsign_key: function (t) {
      sine(rnd(1450, 1800), 1300, 0.022, t, 0.2);
      hi(4500, 4500, 0.01, 1.5, 0.014, t, 0.3, 0);
    },
    callsign_error: function (t) {
      blip(233, 208, 0.09, t, 0.6, 0);
      blip(156, 110, 0.2, t + 0.11, 0.6, 0);
      lo(1200, 700, 0.3, 1, 0.28, 0, 0.2, t, 0.12);
    },
    callsign_ok: function (t) {
      blip(hz(81), hz(81), 0.06, t, 0.32, 0);
      blip(hz(88), hz(88), 0.14, t + 0.065, 0.36, 0);
      wh(900, 5000, 0.25, 1, 0.05, 0.2, 0, 0.2, t, 0.3);
    },
    ui_click: function (t) { blip(1175, 1050, 0.035, t, 0.38, 0); },
    ui_back: function (t) { blip(988, 330, 0.14, t, 0.42, 0); },
    countdown_tick: function (t, d) {
      var n = clamp(Math.round(+d.n || 3), 1, 3), m = n === 3 ? 81 : (n === 2 ? 88 : 95);
      if (d.resume) {
        sine(hz(m), hz(m), 0.09, t, 0.35);
        return;
      }
      sine(hz(m), hz(m), 0.16, t, 0.45);
      bell(m + 12, 0.25, t, 0.12);
      kick(110, 2, 0.03, 0.12, t, 0.2);
    },
    countdown_go: function (t) {
      var i, v, m = GO;
      sweep(G.chordLP.frequency, 9000, 2200, t, 0.35);
      setAt(G.chordLP.Q, 0.8, t);
      for (i = 0; i < 4; i++) {
        v = P.chord[i];
        adsr(v.n.envelope, 0.003, 0.3, 0.25, 0.3);
        setAt(v.n.detune, 0, t);
        tone(v, hz(m[i]), 0.25, t, 0.6);
      }
      wh(6000, 1500, 0.6, 0.8, 0.002, 0.6, 0, 0.5, t, 0.4);
      kick(55, 6, 0.05, 0.4, t, 0.8);
      duck(0.6, 0.1, 0.3, t);
    },
    pause_on: function (t) { blip(880, 196, 0.26, t, 0.45, 0); },
    pause_off: function (t) { blip(330, 1046, 0.12, t, 0.42, 0); },
    sound_on: function (t) {
      blip(hz(84), hz(84), 0.05, t, 0.4, 0);
      blip(hz(91), hz(91), 0.1, t + 0.06, 0.42, 0);
    },
    hint_show: function (t) {
      bell(88, 0.5, t, 0.45);
      bell(95, 0.6, t + 0.09, 0.3);
    },
    lane_shift: function (t, d) {
      var dir = sgn(+d.dir || 0), j = rnd(0.92, 1.08);
      hi(1100 * j, 4200 * j, 0.08, 1.2, 0.085, t, 0.6, dir * 0.55);
      sine(2600, 2000, 0.01, t, 0.16);
    },
    hop: function (t) {
      sine(240, 960, 0.1, t, 0.4);
      hi(2500, 7000, 0.08, 0.8, 0.06, t, 0.22, 0);
    },
    land: function (t) {
      kick(75, 2.5, 0.03, 0.16, t, 0.25);
      lo(500, 300, 0.05, 0, 0.06, 0, 0.04, t, 0.15);
    },
    graze: function (t) {
      var st = PPG.state, c = st && st.combo ? st.combo : 0, m = GRAZE[clamp(c, 0, 9)];
      bell(m, 0.32, t, 0.32);
      bell(m + 12, 0.25, t + 0.035, 0.16);
      hi(8000, 8000, 0.03, 0.7, 0.05, t, 0.22, rnd(-0.3, 0.3));
    },
    crate: function (t, d) {
      var k = clamp(Math.round(+d.mult || 1), 1, 3) - 1, n = CRATE[k], v = pick(P.coin);
      adsr(v.n.envelope, 0.001, 0.18, 0.05, 0.08);
      tone(v, hz(n[0]), 0.05, t, 0.42);
      tone(v, hz(n[1]), 0.16, t + 0.055, 0.48);
      bell(n[1] + 12, 0.3, t + 0.055, 0.18);
    },
    sky_crate: function (t, d) {
      var k = clamp(Math.round(+d.mult || 1), 1, 3) - 1, n = SKY[k], a = P.coin[0], b = P.coin[1];
      adsr(a.n.envelope, 0.001, 0.3, 0.08, 0.15);
      adsr(b.n.envelope, 0.001, 0.4, 0.08, 0.2);
      tone(a, hz(n[0]), 0.06, t, 0.42);
      tone(a, hz(n[1]), 0.3, t + 0.065, 0.48);
      tone(b, hz(n[0] + 12), 0.06, t, 0.22);
      tone(b, hz(n[1] + 12), 0.4, t + 0.065, 0.26);
      bell(n[1] + 12, 0.6, t + 0.065, 0.3);
      wh(6000, 9000, 0.3, 0.8, 0.01, 0.3, 0, 0.2, t, 0.15);
    },
    combo_up: function (t, d) {
      var hi3 = (+d.mult || 2) >= 3, v = pick(P.brass), fe = v.n.filterEnvelope;
      adsr(v.n.envelope, 0.003, 0.1, 0.3, 0.08);
      adsr(fe, 0.002, 0.12, 0.1, 0.1);
      fe.baseFrequency = 500; fe.octaves = 4;
      tone(v, hz(hi3 ? 84 : 81), 0.07, t, 0.65);
      tone(v, hz(hi3 ? 93 : 88), 0.14, t + 0.07, 0.7);
      bell(hi3 ? 105 : 100, 0.3, t + 0.07, 0.12);
    },
    shield_hit: function (t) {
      lo(3500, 600, 0.25, 1, 0.22, 0, 0.16, t, 0.9);
      kick(45, 5, 0.06, 0.35, t, 1);
      bDrive(0.3, 0, t);
      saw(P.sawB, 1400, 70, 0.28, 5000, 400, 1, 0.001, 0.3, 0, 0.05, 0.25, t, 0.55);
      duck(0.35, 0.15, 0.5, t);
    },
    shield_low: function (t) {
      blip(1760, 1660, 0.07, t, 0.6, 0);
      blip(1760, 1660, 0.07, t + 0.15, 0.6, 0);
    },
    shield_pickup: function (t) {
      var i, v, t2, m = UPCH;
      sweep(G.chordLP.frequency, 1200, 4500, t, 0.3);
      setAt(G.chordLP.Q, 1, t);
      for (i = 0; i < 4; i++) {
        v = P.chord[i];
        adsr(v.n.envelope, 0.01, 0.45, 0.3, 0.35);
        t2 = tone(v, hz(m[i]), 0.4, t + i * 0.035, 0.85);
        v.n.detune.cancelScheduledValues(t2);
        v.n.detune.setValueAtTime(-150, t2);
        v.n.detune.linearRampToValueAtTime(0, t2 + 0.12);
      }
      bell(93, 0.5, t + 0.14, 0.25);
    },
    overdrive_on: function (t) {
      bDrive(0.2, 0, t);
      saw(P.sawB, 110, 220, 0.55, 180, 9000, 6, 0.03, 0.4, 0.7, 0.2, 0.55, t, 0.6);
      P.sawB.h = t + 0.6;
      wh(500, 8000, 0.55, 1, 0.45, 0.1, 0, 0.5, t, 0.35);
      sub(160, 38, 0.5, t + 0.55, 1);
      kick(40, 6, 0.08, 0.5, t + 0.55, 0.9);
      duck(0.4, 0.2, 0.6, t + 0.55);
    },
    overdrive_off: function (t) {
      bDrive(0, 0, t);
      saw(P.sawB, 220, 110, 0.5, 7000, 220, 4, 0.005, 0.5, 0, 0.1, 0.45, t, 0.6);
      wh(6000, 500, 0.5, 1, 0.01, 0.5, 0, 0.4, t, 0.3);
    },
    smash: function (t) {
      var f = rnd(1800, 2400);
      metal(f, f * 0.8, 0.3, t, 0.4);
      hi(6000, 6000, 0.2, 0.6, 0.22, t, 0.6, rnd(-0.3, 0.3));
      kick(120, 2, 0.03, 0.12, t, 0.3);
    },
    drone_warn: function (t, d) {
      var pan = 0.6 * sgn(+d.dir || +d.x || 0);
      blip(1568, 1568, 0.035, t, 0.4, pan);
      blip(1568, 1568, 0.035, t + 0.085, 0.4, pan);
    },
    checkpoint_near: function (t) {
      var v;
      saw(P.sawA, 110, 880, 2.4, 300, 6000, 3, 2.1, 0.2, 0.8, 0.15, 2.35, t, 0.5);
      P.sawA.h = t + 2.4;
      v = wh(400, 7000, 2.4, 1.5, 2.2, 0.1, 0.5, 2.35, t, 0.4);
      v.h = t + 2.4;
    },
    checkpoint: function (t, d) {
      var i, v, m = CPCH;
      sweep(G.chordLP.frequency, 8000, 1800, t, 0.5);
      setAt(G.chordLP.Q, 1, t);
      for (i = 0; i < 4; i++) {
        v = P.chord[i];
        adsr(v.n.envelope, 0.004, 0.5, 0.2, 0.4);
        setAt(v.n.detune, 0, t);
        tone(v, hz(m[i]), 0.4, t, 0.62);
      }
      wh(7000, 500, 0.8, 0.8, 0.005, 0.8, 0, 0.6, t, 0.55);
      kick(50, 6, 0.06, 0.45, t, 0.7);
      duck(0.5, 0.2, 0.5, t);
      if (d.flawless) {
        bell(93, 0.4, t + 0.15, 0.25);
        bell(100, 0.5, t + 0.25, 0.2);
      }
    },
    tier_unlock: function (t, d) {
      var lv = clamp(Math.round(+d.tier || 1), 1, 3), r = 69 + 2 * (lv - 1), a = P.brass[0], b = P.brass[1], i;
      for (i = 0; i < 2; i++) {
        adsr(P.brass[i].n.envelope, 0.02, 0.3, 0.6, 0.3);
        adsr(P.brass[i].n.filterEnvelope, 0.03, 0.3, 0.5, 0.3);
        P.brass[i].n.filterEnvelope.baseFrequency = 300;
        P.brass[i].n.filterEnvelope.octaves = 3.5;
      }
      tone(a, hz(r), 0.08, t, 0.5);
      tone(a, hz(r + 4), 0.08, t + 0.1, 0.5);
      tone(a, hz(r + 7), 0.45, t + 0.2, 0.55);
      tone(b, hz(r - 5), 0.08, t, 0.32);
      tone(b, hz(r), 0.08, t + 0.1, 0.32);
      tone(b, hz(r + 4), 0.45, t + 0.2, 0.35);
      bell(r + 24, 0.6, t + 0.2, 0.25);
    },
    sector: function (t) { wh(300, 7000, 1.2, 4, 0.15, 1.0, 0, 1.1, t, 0.85); },
    boss_warning: function (t) {
      var f = P.sawA.n.frequency, k, c;
      saw(P.sawA, 311, 311, 0.01, 2200, 2200, 2, 0.02, 0.1, 0.9, 0.1, 1.65, t, 0.9);
      t = P.sawA.t;
      for (k = 0; k < 2; k++) {
        c = t + k * 0.85;
        f.setValueAtTime(311, c);
        f.exponentialRampToValueAtTime(466, c + 0.3);
        f.setValueAtTime(466, c + 0.62);
        f.exponentialRampToValueAtTime(300, c + 0.8);
      }
      P.sawA.h = t + 1.7;
    },
    boss_arrive: function (t) {
      sub(110, 26, 1.2, t, 1);
      kick(38, 6, 0.12, 0.9, t, 1);
      metal(120, 90, 1.2, t, 0.6);
      lo(2500, 150, 0.9, 0.5, 0.9, 0, 0.63, t, 0.85);
      duck(0.2, 0.4, 1.0, t);
    },
    lance_lock: function (t) {
      saw(P.sawA, 500, 2200, 0.9, 1200, 4000, 8, 0.25, 0.1, 0.9, 0.05, 0.88, t, 0.3);
      P.sawA.h = t + 0.95;
    },
    lance_fire: function (t) {
      var v;
      bDrive(0.7, 0, t);
      saw(P.sawB, 220, 150, 0.5, 3000, 3000, 1, 0.003, 0.2, 0.6, 0.08, 0.45, t, 0.6);
      P.sawB.h = t + 0.5;
      v = lo(6000, 1500, 0.5, 0.6, 0.3, 0.4, 0.45, t, 0.5);
      v.h = t + 0.5;
      duck(0.6, 0.3, 0.3, t);
    },
    curtain: function (t) {
      var f;
      bDrive(0.1, 0, t);
      saw(P.sawB, 55, 55, 0.01, 250, 250, 10, 0.4, 0.3, 0.6, 0.3, 0.8, t, 0.6);
      f = P.sawB.f.frequency;
      t = P.sawB.t;
      f.exponentialRampToValueAtTime(900, t + 0.35);
      f.exponentialRampToValueAtTime(300, t + 0.9);
      P.sawB.h = t + 0.9;
      wh(200, 1200, 0.6, 6, 0.3, 0.6, 0, 0.5, t, 0.25).h = t + 0.9;
    },
    sweep_warn: function (t, d) {
      setAt(P.tickB.p.pan, -0.6 * sgn(+d.dir || 0), t);
      setAt(G.tickBo.gain, 0.8, t);
      setAt(G.tickBn.gain, 1.3, t);
      setAt(P.tickB.f.frequency, 1600, t);
      setAt(P.tickB.f.Q, 2, t);
      setAt(P.tickB.k.frequency, 150, t);
      ticks(P.tickB, null, t, 0.9, 11, 20, 0, 0, 0.9, false, 0.04);
    },
    sweep: function (t, d) {
      var shots = PPG.boss && PPG.boss.shots, i, s, dir = sgn(+d.dir || 0), dur = 1.4, p;
      if (shots) {
        for (i = 0; i < shots.length; i++) {
          s = shots[i];
          if (s && s.active && s.kind === 'sweep' && s.vx) {
            if (!dir) { dir = sgn(s.vx); }
            dur = clamp(11 / Math.abs(s.vx), 0.6, 1.8);
            break;
          }
        }
      }
      if (!dir) { dir = (P.sawB.t * 10) % 2 < 1 ? 1 : -1; }
      bDrive(0.6, -0.8 * dir, t);
      saw(P.sawB, 82, 70, dur, 1800, 1800, 1, 0.02, 0.1, 0.8, 0.1, dur, t, 0.55);
      t = P.sawB.t;
      p = P.sawB.p.pan;
      p.linearRampToValueAtTime(0.8 * dir, t + dur);
      P.sawB.h = t + dur + 0.1;
      lo(900, 900, 0.1, 0.3, 0.1, 0.7, dur, t, 0.4).h = t + dur + 0.1;
    },
    scatter: function (t) {
      var v = pick(P.kick);
      v.n.octaves = 1.6;
      v.n.pitchDecay = 0.02;
      adsr(v.n.envelope, 0.001, 0.2, 0, 0.05);
      tone(v, 150, 0.12, t, 0.6);
      tone(v, 125, 0.12, t + 0.06, 0.55);
      tone(v, 175, 0.12, t + 0.12, 0.5);
      wh(700, 700, 0.1, 3, 0.005, 0.2, 0, 0.1, t, 0.2);
    },
    scatter_land: function (t) {
      sine(900, 220, 0.04, t, 0.4);
      hi(3000, 3000, 0.02, 1, 0.025, t, 0.4, 0);
    },
    core_open: function (t) {
      wh(6000, 2500, 0.5, 0.7, 0.04, 0.45, 0, 0.4, t, 0.45);
      bell(88, 0.6, t + 0.1, 0.4);
      bell(93, 0.8, t + 0.2, 0.35);
    },
    strike: function (t) {
      bDrive(0.2, 0, t);
      saw(P.sawB, 220, 1760, 0.12, 400, 7000, 6, 0.003, 0.15, 0, 0.05, 0.12, t, 0.5);
      kick(50, 5, 0.06, 0.4, t + 0.1, 1);
      hi(2500, 2500, 0.05, 0.5, 0.08, t + 0.1, 0.7, 0);
      duck(0.5, 0.15, 0.35, t);
    },
    boss_hit: function (t, d) {
      var hp = clamp(+d.hp || 0, 0, 4), f = 1800 + (4 - hp) * 220;
      kick(36, 6, 0.12, 0.9, t, 1);
      lo(4000, 200, 0.7, 0.8, 0.7, 0, 0.5, t, 0.9);
      metal(f, f * 0.55, 0.45, t, 0.4);
      duck(0.3, 0.3, 0.7, t);
    },
    ring_miss: function (t) {
      var v = pick(P.blip);
      adsr(v.n.envelope, 0.01, 0.25, 0.5, 0.15);
      setAt(G.blipPan.pan, 0, t);
      slide(v, hz(76), hz(76), 0.1, 0.18, t, 0.55);
      slide(v, hz(72), hz(71), 0.35, 0.35, t + 0.22, 0.55);
    },
    boss_crack: function (t) {
      setAt(P.tickB.p.pan, 0, t);
      setAt(G.tickBo.gain, 0, t);
      setAt(G.tickBn.gain, 1.4, t);
      setAt(P.tickB.f.frequency, 4000, t);
      setAt(P.tickB.f.Q, 0.8, t);
      ticks(P.tickB, null, t, 0.9, 10, 34, 0, 0, 0.8, true, 0.025);
      sine(900, 2700, 0.85, t, 0.15);
    },
    boss_blast_small: function (t) {
      kick(rnd(45, 75), 4, 0.08, 0.45, t, 0.9);
      lo(2500, 300, 0.35, 0.5, 0.35, 0, 0.25, t, 0.7);
      duck(0.5, 0.1, 0.3, t);
    },
    boss_implode: function (t) {
      var v = pick(P.wh);
      v.h = 0;
      nz(v, 300, 6000, 0.25, 0.8, 0.24, 0.01, 0, 0.01, 0.25, t, 0.8, 'exponential');
      sine(70, 900, 0.24, t, 0.5);
    },
    boss_blast: function (t) {
      sub(75, 24, 1.8, t, 1);
      kick(32, 8, 0.18, 1.2, t, 1);
      lo(9000, 180, 2.4, 0.7, 2.4, 0, 1.8, t, 1);
      wh(2000, 200, 2.5, 0.5, 0.01, 2.5, 0, 2.2, t, 0.7);
      metal(90, 60, 1.6, t, 0.5);
      setAt(P.tickB.p.pan, 0, t);
      setAt(G.tickBo.gain, 0, t);
      setAt(G.tickBn.gain, 1.4, t);
      setAt(P.tickB.f.frequency, 3500, t);
      setAt(P.tickB.f.Q, 0.7, t);
      ticks(P.tickB, null, t + 0.2, 1.6, 30, 6, 0, 0, 0.6, true, 0.03);
      duck(0.12, 0.6, 1.6, t);
    },
    warp: function (t) {
      var v;
      saw(P.sawA, 55, 440, 2.0, 250, 7000, 2, 0.25, 0.1, 0.9, 0.25, 1.9, t, 0.55);
      P.sawA.h = t + 2.0;
      v = wh(500, 6000, 2.0, 1, 1.5, 0.2, 0.6, 1.9, t, 0.45);
      v.h = t + 2.0;
    },
    wreck: function (t) {
      lo(5000, 800, 0.3, 1, 0.3, 0, 0.2, t, 1);
      kick(42, 6, 0.1, 1.0, t, 1);
      wh(1200, 120, 1.4, 0.6, 0.005, 1.4, 0, 1.2, t, 0.8);
      bDrive(0.4, 0, t);
      saw(P.sawB, 330, 28, 0.75, 2400, 200, 2, 0.002, 0.8, 0.4, 0.1, 0.75, t, 0.5);
      metal(700, 300, 0.4, t, 0.3);
      duck(0.25, 0.4, 1.2, t);
    },
    game_over: function (t) {
      var a = P.brass[0], b = P.brass[1], i, m = OVER, w = OVERT, du = OVERD;
      for (i = 0; i < 2; i++) {
        adsr(P.brass[i].n.envelope, 0.01, 0.3, 0.6, 0.4);
        adsr(P.brass[i].n.filterEnvelope, 0.02, 0.4, 0.4, 0.4);
        P.brass[i].n.filterEnvelope.baseFrequency = 600;
        P.brass[i].n.filterEnvelope.octaves = 2;
      }
      for (i = 0; i < 3; i++) {
        tone(a, hz(m[i]), du[i], t + w[i], 0.62);
        tone(b, hz(m[i] - 12), du[i], t + w[i], 0.38);
      }
    },
    score_roll: function (t) {
      ticks(P.tickA, P.tickA.k, t, 1.2, 26, 26, 900, 1800, 0.45, false, 0.02);
      setAt(P.tickA.f.frequency, 2400, t);
      setAt(P.tickA.f.Q, 1, t);
      P.tickA.mode = 'roll';
    },
    score_lock: function (t) {
      if (P.tickA.mode === 'roll') { cut(P.tickA, t); }
      sine(2093, 1760, 0.03, t, 0.35);
      kick(180, 1.5, 0.01, 0.06, t, 0.2);
      bell(93, 0.6, t + 0.02, 0.3);
      bell(100, 0.7, t + 0.08, 0.2);
    },
    decrypt: function (t) {
      var v = P.tickA, start = (v.mode === 'decrypt' && v.h > t) ? v.s0 : t;
      var end = Math.min(t + 0.85, start + 1.5);
      if (end <= t) { return; }
      setAt(v.f.frequency, 2800, t);
      setAt(v.f.Q, 1, t);
      ticks(v, v.k, t, end - t, 30, 30, 1046, 2349, 0.4, true, 0.022);
      v.mode = 'decrypt';
      v.s0 = start;
    },
    code_reveal: function (t) {
      sine(1568, 2349, 0.03, t, 0.35);
      bell(93, 0.4, t, 0.45);
      bell(100, 0.6, t + 0.06, 0.3);
      hi(9000, 9000, 0.3, 0.5, 0.35, t, 0.2, 0);
    },
    copy: function (t) {
      blip(1568, 1568, 0.04, t, 0.45, 0);
      blip(2093, 2093, 0.04, t + 0.07, 0.45, 0);
    },
    board_in: function (t) {
      bell(81, 0.3, t, 0.4);
      bell(84, 0.3, t + 0.07, 0.4);
      bell(88, 0.3, t + 0.14, 0.4);
      bell(93, 0.5, t + 0.21, 0.45);
    },
    new_record: function (t) {
      var a = P.brass[0], b = P.brass[1], i, m = UPCH;
      for (i = 0; i < 2; i++) {
        adsr(P.brass[i].n.envelope, 0.02, 0.3, 0.6, 0.3);
        adsr(P.brass[i].n.filterEnvelope, 0.03, 0.3, 0.5, 0.3);
        P.brass[i].n.filterEnvelope.baseFrequency = 300;
        P.brass[i].n.filterEnvelope.octaves = 3.5;
      }
      for (i = 0; i < 4; i++) {
        tone(a, hz(m[i]), i === 3 ? 0.5 : 0.08, t + i * 0.09, 0.5);
        tone(b, hz(m[i] - 5), i === 3 ? 0.5 : 0.08, t + i * 0.09, 0.3);
      }
      bell(93, 0.6, t + 0.27, 0.3);
      bell(100, 0.7, t + 0.36, 0.2);
    }
  };
  /* Per-id cooldowns in seconds of audio time (design.md section 15 names the first three). */
  var CD = {
    lane_shift: 0.04, graze: 0.06, crate: 0.03, callsign_key: 0.02, hop: 0.06, land: 0.06,
    smash: 0.05, scatter: 0.1, scatter_land: 0.05, ui_click: 0.04, copy: 0.1, decrypt: 0.04,
    code_reveal: 0.05, sky_crate: 0.05, drone_warn: 0.08, boss_blast_small: 0.08,
    countdown_tick: 0.1, shield_hit: 0.08, sweep_warn: 0.3, lance_lock: 0.3, curtain: 0.3
  };

  /* ---------- the public API ---------- */

  function readMuted() {
    if (mutedRead) { return; }
    mutedRead = true;
    try { muted = window.sessionStorage.getItem('pineParkMuted') === '1'; } catch (e) { muted = false; }
  }
  function cutSustained(now) {
    var i;
    for (i = 0; i < SUS.length; i++) { if (SUS[i].h > now) { cut(SUS[i], now); } }
  }
  function suspendNow() {
    suspendId = -1;
    if (!halted || X.isOffline) { return; }
    try {
      var r = X.rawContext, p;
      if (r && r.state === 'running' && r.suspend) {
        p = r.suspend();
        if (p && p.then) { p.then(noop, noop); }
      }
    } catch (e) { /* the context stays awake; it is silent anyway */ }
  }
  function wake() {
    var c = X, now = c.currentTime, p;
    halted = false;
    if (suspendId >= 0) { c.clearTimeout(suspendId); suspendId = -1; }
    lvl(G.sfxGate, 1, now, 0.03);
    lvl(G.verbRet, 1, now, 0.03);
    lvl(G.bg, 1, now, 0.03);
    if (c.state === 'suspended' && !c.isOffline) {
      try { p = c.resume(); if (p && p.then) { p.then(noop, noop); } } catch (e) { /* next gesture */ }
    }
  }

  /* Tone 14.8.49's Oscillator makes a one-shot node per note and links its own frequency
     and detune signals into it. Tone's disconnect never resolves a Param, so those links
     outlived the node and kept every note reachable (about 4 MB a full run, TARGET 2).
     Each one-shot now unlinks them as it ends, when its output is already silent.
     Each Param also keeps its last 1000 automation events and each source its last 100
     states; 48 and 24 are plenty for the 0.12 s look-ahead and stop the long climb as
     rarely used voices fill up run after run. */
  function unlink(sig, prm) {
    try { if (sig && prm && prm.input) { sig.disconnect(prm.input); } } catch (e) { }
  }
  function fixOsc() {
    var pr = T.Oscillator && T.Oscillator.prototype, st = pr && pr._start;
    var tl = T.Timeline && T.Timeline.prototype, add = tl && tl.add;
    if (typeof add === 'function' && !tl.ppgCap) {
      tl.ppgCap = true;
      tl.add = function (ev) {
        if (this.memory === 1000) { this.memory = 48; } else if (this.memory === 100) { this.memory = 24; }
        return add.call(this, ev);
      };
    }
    if (typeof st !== 'function' || pr.ppgUnlink) { return; }
    pr.ppgUnlink = true;
    pr._start = function (time) {
      var me = this, o, end;
      st.call(this, time);
      o = this._oscillator;
      if (!o || typeof o.onended !== 'function') { return; }
      end = o.onended;
      o.onended = function (n) {
        unlink(me.frequency, n.frequency);
        unlink(me.detune, n.detune);
        end(n);
      };
    };
  }

  function init() {
    var Tn = window.Tone;
    if (ready) { return true; }
    if (failed) { return false; }
    if (!Tn || typeof Tn.Synth !== 'function' || typeof Tn.getContext !== 'function') { return false; }
    readMuted();
    try {
      T = Tn;
      X = Tn.getContext();
      X.lookAhead = 0.02;
      fixOsc();
      build();
      X.on('tick', onTick);
      ready = true;
    } catch (e) {
      failed = true;
      ready = false;
      warn('init', 'init failed; the game runs silent');
      return false;
    }
    if (trackId !== 'off') { want = trackId; }
    return true;
  }

  function start() {
    var Tn = window.Tone, p;
    if (!Tn || typeof Tn.start !== 'function') { return; }
    try {
      if (!ready && !failed) { init(); }
      if (ready && halted) { wake(); }
      p = Tn.start();
      if (p && typeof p.then === 'function') { p.then(noop, noop); }
    } catch (e) { warn('start', 'audio did not start'); }
  }

  function play(id, data) {
    var c, now, last;
    if (id === 'intro' && !muted && (!ready || !X || X.state !== 'running')) { introAt = nowMs(); }
    if (!ready || typeof id !== 'string' || !has.call(SFX, id)) { return; }
    if (muted) { return; }
    try {
      if (halted) {
        if (!screenAwake()) { return; }
        wake();
      }
      c = X;
      if (c.state !== 'running') { return; }
      now = c.currentTime;
      if (!live) { goLive(now + 0.005); }
      last = lastAt[id];
      if (last !== undefined && now >= last && now - last < (has.call(CD, id) ? CD[id] : 0.03)) { return; }
      lastAt[id] = now;
      lastId = id;
      SFX[id](X.now(), data && typeof data === 'object' ? data : EMPTY);
    } catch (e) { warn('play:' + id, 'sound ' + id + ' failed'); }
  }

  /* A phase track asked for once the boss has begun is stale (call order at a pp_test
     boss start is not fixed), so it maps to what the boss stage wants instead. */
  function bossTrack() {
    var b = PPG.boss && PPG.boss.state, st = b && b.stage;
    if (st === 'warning' || st === 'entrance') { return 'warning'; }
    if (st === 'round' || st === 'open') { return 'boss'; }
    return '';
  }
  function music(id) {
    if (typeof id !== 'string' || !has.call(TR, id)) { return; }
    if (id.indexOf('phase_') === 0 && PPG.state && PPG.state.phase === 'boss') {
      id = bossTrack();
      if (!id) { return; }
    }
    if (!ready) { trackId = id; return; }
    try {
      if (id === 'off') { trackId = 'off'; musicOff(); return; }
      if (halted) {
        if (!screenAwake()) { return; }
        wake();
      }
      trackId = id;
      /* a track change waits for a running context, so no source starts while it is locked */
      if (X.state === 'running') { want = ''; route(id); } else { want = id; }
    } catch (e) { warn('music', 'music failed'); }
  }

  function setMuted(b) {
    readMuted();
    muted = !!b;
    try { window.sessionStorage.setItem('pineParkMuted', muted ? '1' : '0'); } catch (e) { /* private mode */ }
    if (ready) {
      try { lvl(G.mute, muted ? 0 : 1, X.currentTime, 0.03); } catch (e2) { warn('mute', 'mute failed'); }
    }
  }
  function isMuted() { readMuted(); return muted; }

  /* bg true: the tab is hidden on a screen that cannot pause (wreck, results), so
     the whole mix fades out until resumeAll (R8, gate 3). */
  function pauseAll(bg) {
    if (!ready) { return; }
    try {
      var now = X.currentTime;
      if (bg) { lvl(G.bg, 0, now, 0.06); }
      seq.paused = true;
      cutSustained(now);
      lvl(G.pause, 0.32, now, 0.12);
      lvl(G.musSend, 0.3, now, 0.12);
      G.musLP.frequency.exponentialRampTo(650, 0.18, now);
    } catch (e) { warn('pause', 'pause failed'); }
  }
  function resumeAll() {
    if (!ready) { return; }
    try {
      var now = X.currentTime;
      lvl(G.bg, 1, now, 0.2);
      lvl(G.pause, 1, now, 0.25);
      lvl(G.musSend, 1, now, 0.25);
      G.musLP.frequency.exponentialRampTo(18000, 0.3, now);
      if (seq.paused) { seq.paused = false; seq.next = 0; }
    } catch (e) { warn('resume', 'resume failed'); }
  }
  function stopAll() {
    introAt = -1;
    if (!ready) { return; }
    try {
      var c = X, now = c.currentTime;
      halted = true;
      seq.on = false;
      seq.paused = false;
      seq.pending = '';
      seq.track = 'off';
      trackId = 'off';
      want = '';
      cutSustained(now);
      releaseMusic(now);
      lvl(G.sfxGate, 0, now + 0.05, 0.12);
      lvl(G.musGate, 0, now, 0.1);
      lvl(G.verbRet, 0, now + 0.03, 0.15);
      lvl(G.pause, 1, now, 0.05);
      lvl(G.musSend, 1, now, 0.05);
      G.musLP.frequency.exponentialRampTo(18000, 0.05, now);
      if (live) { goQuiet(now + 0.2); }
      if (suspendId < 0 && !c.isOffline) { suspendId = c.setTimeout(suspendNow, 0.4); }
    } catch (e) { warn('stop', 'stop failed'); }
  }

  function state() {
    var tn = PPG.loader && PPG.loader.tone ? PPG.loader.tone : (window.Tone ? 'loaded' : 'idle');
    return {
      ready: ready,
      running: !!(ready && X.state === 'running'),
      muted: isMuted(),
      tone: tn,
      track: trackId,
      lastId: lastId
    };
  }
  function master() { return ready ? G.master : null; }

  PPG.audio = {
    init: init,
    start: start,
    play: play,
    music: music,
    setMuted: setMuted,
    isMuted: isMuted,
    pauseAll: pauseAll,
    resumeAll: resumeAll,
    stopAll: stopAll,
    state: state,
    master: master
  };
})();
